/**
 * Spec 200 Fase 6 #2 — Settings route.
 *
 * Refactor (panel-based, URL-routed): the surface is split into
 * focused sub-routes the same way Linear/Notion/GitHub/Vercel do
 * Settings. Each section is its own URL so deep-links survive a
 * refresh, the back/forward buttons work, and Cmd+K can land
 * directly on a panel.
 *
 *   /settings              → mobile index (or redirects to /perfil
 *                            on desktop via <SettingsIndexRedirect/>)
 *   /settings/perfil
 *   /settings/equipo
 *   /settings/apariencia
 *   /settings/identidad        (admin/creator only — sender info
 *                               that appears on every PDF header)
 *   /settings/privacidad
 *   /settings/sesiones-web     (admin/creator only)
 *   /settings/cuenta           (logout lives here)
 *
 * The exported component shape is:
 *   - <SettingsLayout>     — header + aside nav + <Outlet/>. Mounted
 *                            at /settings via App.tsx.
 *   - Panel components     — one per route, named SettingsProfile,
 *                            SettingsTeam, etc. Each panel keeps the
 *                            historic data-testids so unit + e2e
 *                            tests don't have to re-learn the page.
 *   - <SettingsIndexMobile/> — the iOS-style index list shown at
 *                              /settings on narrow viewports.
 *   - <SettingsIndexRedirect/> — at /settings on wide viewports,
 *                                redirects to /settings/perfil.
 *
 * Why localStorage for theme + font size:
 *   These preferences are device-local UI state, not E2EE business
 *   data. Reading them synchronously at first paint avoids the FOUC
 *   that an async IndexedDB read would introduce. The identity row
 *   stays in IndexedDB.
 */
import { useEffect, useRef, useState } from 'react';
import {
  Navigate,
  NavLink,
  Outlet,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import * as Popover from '@radix-ui/react-popover';
import {
  Building2,
  ChevronLeft,
  ChevronRight,
  CircleUser,
  Globe,
  Info,
  LogOut,
  Monitor,
  Moon,
  Settings as SettingsIcon,
  ShieldCheck,
  Sun,
  Trash2,
  Type,
  Users,
} from 'lucide-react';

import { BackupStatusCard } from '../components/BackupStatusCard';
import { EmptyState } from '../components/EmptyState';
import { RELAY_HTTP_URL } from '../config/env';
import {
  SUPPORTED_LOCALES,
  currentLocale,
  setLocale,
  type SupportedLocale,
} from '../i18n';
import { relay, type WorkerListEntry } from '../sync/relay';
import { Avatar } from '../components/ui/Avatar';
import { clearAll } from '../store/db';
import { clearIdentity, type WebIdentity } from '../store/keys';
import {
  FONT_SIZE_STORAGE_KEY,
  THEME_STORAGE_KEY,
  type FontSizePreference,
  type ThemePreference,
  readFontSizePreference,
  readThemePreference,
} from '../theme/preferences';

import './Settings.css';

interface SettingsLayoutProps {
  identity: WebIdentity;
  /**
   * Spec 217 PR4 — invoked after the local IDB + identity have been
   * wiped on a Cerrar sesión click. The layout itself doesn't fire
   * logout (that lives in <SettingsAccount/>); the prop stays on the
   * layout API for backward-compatibility with App.tsx, which routes
   * the same callback into the account panel via its own <Route>.
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- see comment above
  onLogout?: () => void;
}

interface PanelProps {
  identity: WebIdentity;
}

interface AccountPanelProps extends PanelProps {
  onLogout?: () => void;
}

interface WebTerminalRow {
  id: string;
  technician_id: string;
  user_agent: string | null;
  paired_at: string | number;
  last_seen_at: string | number;
}

const ROLE_LABEL: Record<WebIdentity['role'], string> = {
  creator: 'Propietaria',
  admin: 'Administradora',
  member: 'Miembro',
};

/** Detects relay workerIds that belong to a paired browser tab rather
 *  than a real teammate. The relay assigns a `-web-XXXXXXXX` suffix
 *  (8 hex chars) to every web terminal so it can route messages back
 *  to the right tab. We exclude those from the "Miembros" list — they
 *  live in the dedicated "Sesiones web" panel — and we use the inverse
 *  match in the same file to keep the two views consistent. */
const WEB_TERMINAL_ID_RE = /-web-[0-9a-f]{8}$/i;

function formatPairedAt(ts: number | string): string {
  const n = typeof ts === 'number' ? ts : Date.parse(ts);
  if (!Number.isFinite(n)) return '—';
  return new Date(n).toLocaleString('es-ES', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** Shorten a verbose user-agent string into "Browser · OS" for the
 *  terminals list. We don't need a perfect parser — Chrome, Firefox,
 *  Safari, Edge cover ~98% of paired browsers, and an unknown UA
 *  falls through to a sensible "Otro navegador" label. */
function summariseUserAgent(ua: string | null | undefined): string {
  if (!ua) return 'Navegador desconocido';
  let browser = 'Otro navegador';
  if (/Edg\//.test(ua)) browser = 'Edge';
  else if (/Firefox\//.test(ua)) browser = 'Firefox';
  else if (/Chrome\//.test(ua)) browser = 'Chrome';
  else if (/Safari\//.test(ua)) browser = 'Safari';
  let os = '';
  if (/Windows NT/.test(ua)) os = 'Windows';
  else if (/Mac OS X/.test(ua)) os = 'macOS';
  else if (/Android/.test(ua)) os = 'Android';
  else if (/iPhone|iPad|iOS/.test(ua)) os = 'iOS';
  else if (/Linux/.test(ua)) os = 'Linux';
  return os ? `${browser} · ${os}` : browser;
}

/* ───────────────────────────── Layout ─────────────────────────── */

/**
 * Top-level Settings shell. Mounts at `/settings`; nests the panel
 * routes underneath so the URL is the source of truth for which
 * section is visible.
 *
 * Layout (>=1100px):  aside nav ▎  panel
 * Layout (<1100px):   index OR  panel + back link  (iOS pattern)
 *
 * The CSS does the heavy lifting via `data-on-section`. The wrapper
 * sets `data-on-section="true"` on every URL except `/settings` exact;
 * mobile CSS uses that to swap which child is visible.
 */
export function Settings({ identity }: SettingsLayoutProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const isAdmin = identity.role === 'creator' || identity.role === 'admin';

  // /settings exact = on the index. Anything below is "on a section",
  // which the mobile CSS uses to swap which child is visible.
  const onIndex =
    location.pathname === '/settings' || location.pathname === '/settings/';

  // Desktop guard: at >=1100 px we never show the iOS-style index list —
  // bounce the user to /settings/perfil so a panel is always visible on
  // the right. A media-query check at render time would force a hydration
  // mismatch in tests; doing it in an effect keeps the first paint stable.
  useEffect(() => {
    if (!onIndex) return;
    if (typeof window === 'undefined') return;
    if (window.matchMedia('(min-width: 1100px)').matches) {
      navigate('/settings/perfil', { replace: true });
    }
  }, [onIndex, navigate]);

  return (
    <div
      className="settings-shell"
      data-testid="settings-screen"
      data-on-section={onIndex ? 'false' : 'true'}
    >
      <header className="settings-header">
        <button
          type="button"
          className="settings-back"
          onClick={() => {
            // On a section in mobile, "Volver" returns to the index.
            // On the index (or desktop), it pops to the previous
            // route. We detect by the same flag the layout uses.
            if (!onIndex && window.matchMedia('(max-width: 1099px)').matches) {
              navigate('/settings');
            } else {
              navigate(-1);
            }
          }}
          data-testid="settings-back"
        >
          <ChevronLeft size={20} strokeWidth={2} aria-hidden="true" />
          Volver
        </button>
        <h1 className="settings-title">
          <SettingsIcon size={20} strokeWidth={2} aria-hidden="true" />
          Ajustes
        </h1>
      </header>

      <aside className="settings-nav" aria-label="Navegación de ajustes">
        <NavLink to="/settings/perfil" className="settings-nav-link">
          <CircleUser size={18} strokeWidth={2} aria-hidden="true" />
          {t('settings.profile')}
        </NavLink>
        <NavLink to="/settings/equipo" className="settings-nav-link">
          <Users size={18} strokeWidth={2} aria-hidden="true" />
          {t('settings.team')}
        </NavLink>
        <NavLink to="/settings/apariencia" className="settings-nav-link">
          <Sun size={18} strokeWidth={2} aria-hidden="true" />
          {t('settings.appearance')}
        </NavLink>
        {isAdmin && (
          <NavLink to="/settings/identidad" className="settings-nav-link">
            <Building2 size={18} strokeWidth={2} aria-hidden="true" />
            Identidad de empresa
          </NavLink>
        )}
        <NavLink to="/settings/privacidad" className="settings-nav-link">
          <ShieldCheck size={18} strokeWidth={2} aria-hidden="true" />
          Privacidad
        </NavLink>
        {isAdmin && (
          <NavLink to="/settings/sesiones-web" className="settings-nav-link">
            <Globe size={18} strokeWidth={2} aria-hidden="true" />
            Sesiones web
          </NavLink>
        )}
        <NavLink to="/settings/cuenta" className="settings-nav-link">
          <LogOut size={18} strokeWidth={2} aria-hidden="true" />
          Cuenta
        </NavLink>
      </aside>

      <main id="main-content" className="settings-main" tabIndex={-1}>
        {/* The visible <h1 className="settings-title"> lives inside
            the route header, which the AppShell hides when running
            paired (body.has-app-shell rule). axe-core then flags
            page-has-heading-one. Mirror the WorkList pattern: add an
            sr-only h1 here so assistive tech still gets a route
            label. */}
        <h1 className="sr-only">{t('settings.title')}</h1>
        {onIndex ? (
          // Mobile-only iOS index list. Desktop never sees this branch
          // because <SettingsIndexRedirect/> bounces the user to
          // /settings/perfil before this render commits.
          <SettingsIndexMobile identity={identity} />
        ) : (
          <div
            className="settings-panel-wrapper"
            // Re-keying on pathname makes the cross-fade fire when the
            // panel changes. A string key is enough — we just need
            // React to remount the wrapper to retrigger the CSS
            // keyframe.
            key={location.pathname}
          >
            <Outlet />
          </div>
        )}
      </main>
    </div>
  );
}

/**
 * Default landing for `/settings`. On wide viewports we want a panel
 * on the right immediately; redirecting to /settings/perfil keeps
 * the URL as the single source of truth (no special "no panel" empty
 * state). On narrow viewports we keep the user on /settings and the
 * layout swaps to the index list — done in CSS so the same React
 * tree handles both.
 */
export function SettingsIndexRedirect() {
  // We can't conditionally redirect server-side. Easiest: always
  // redirect to /perfil; the mobile-index UI is rendered by the
  // layout itself when location.pathname === '/settings'. Wait —
  // that's a contradiction (we'd never see mobile index after the
  // redirect). Resolution: only redirect on desktop; on mobile
  // render nothing here (the layout shows the index list).
  if (typeof window !== 'undefined' && window.matchMedia('(min-width: 1100px)').matches) {
    return <Navigate to="/settings/perfil" replace />;
  }
  return null;
}

/* ─────────────────────────── Mobile index ──────────────────────── */

/**
 * iOS-style index list. Visible only at /settings on narrow
 * viewports — the layout's `data-on-section="false"` flag controls
 * the CSS branch.
 */
function SettingsIndexMobile({ identity }: PanelProps) {
  const { t } = useTranslation();
  const isAdmin = identity.role === 'creator' || identity.role === 'admin';

  const items: { to: string; label: string; Icon: typeof CircleUser }[] = [
    { to: '/settings/perfil', label: t('settings.profile'), Icon: CircleUser },
    { to: '/settings/equipo', label: t('settings.team'), Icon: Users },
    { to: '/settings/apariencia', label: t('settings.appearance'), Icon: Sun },
    ...(isAdmin
      ? [{ to: '/settings/identidad', label: 'Identidad de empresa', Icon: Building2 }]
      : []),
    { to: '/settings/privacidad', label: 'Privacidad', Icon: ShieldCheck },
    ...(isAdmin
      ? [{ to: '/settings/sesiones-web', label: 'Sesiones web', Icon: Globe }]
      : []),
    { to: '/settings/cuenta', label: 'Cuenta', Icon: LogOut },
  ];

  return (
    <ul className="settings-index" data-testid="settings-index">
      {items.map(({ to, label, Icon }) => (
        <li key={to}>
          <NavLink
            to={to}
            className="settings-index-item"
            data-testid={`settings-index-${to.split('/').pop()}`}
          >
            <Icon size={20} strokeWidth={2} aria-hidden="true" />
            <span className="settings-index-label">{label}</span>
            <ChevronRight size={18} strokeWidth={2} aria-hidden="true" />
          </NavLink>
        </li>
      ))}
    </ul>
  );
}

/* ─────────────────────────── Profile ───────────────────────────── */

export function SettingsProfile({ identity }: PanelProps) {
  return (
    <section className="settings-section" data-testid="settings-profile">
      <h2 className="settings-h2">Perfil</h2>
      <dl className="settings-kv">
        <dt>Nombre</dt>
        <dd data-testid="settings-profile-name">{identity.name}</dd>
        <dt>Rol</dt>
        <dd>
          <span
            className={`settings-role-badge settings-role-badge--${identity.role}`}
            data-testid="settings-profile-role"
          >
            {ROLE_LABEL[identity.role]}
          </span>
        </dd>
        <dt>ID empresa</dt>
        <dd className="settings-mono" data-testid="settings-profile-company">
          {identity.companyId}
        </dd>
        <dt>Emparejado</dt>
        <dd data-testid="settings-profile-paired">{formatPairedAt(identity.pairedAt)}</dd>
      </dl>
    </section>
  );
}

/* ───────────────────────────── Team ────────────────────────────── */

export function SettingsTeam({ identity }: PanelProps) {
  const { t } = useTranslation();
  const isCreator = identity.role === 'creator';

  const [teamName, setTeamName] = useState<string>('');
  const [teamNameDraft, setTeamNameDraft] = useState<string>('');
  const [teamNameBusy, setTeamNameBusy] = useState(false);
  const [teamNameError, setTeamNameError] = useState<string | null>(null);
  const [teamNameSaved, setTeamNameSaved] = useState(false);

  // Spec — team members. The relay keeps the canonical roster; we
  // request it on mount and refresh on every `workers` broadcast so
  // promotions, joins and renames stay live without a manual reload.
  const [members, setMembers] = useState<WorkerListEntry[]>([]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const res = await fetch(`${RELAY_HTTP_URL}/companies/me`, {
          headers: { 'x-company-token': identity.companyToken },
        });
        if (!res.ok) return;
        const body = (await res.json()) as { id: string; name: string | null };
        if (cancelled) return;
        const value = body.name ?? '';
        setTeamName(value);
        setTeamNameDraft(value);
      } catch {
        // Network or relay down — leave the input empty rather than
        // erroring out the whole Settings panel.
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [identity.companyToken]);

  // Live refresh when another device (the creator from mobile, or
  // another web tab) renames the team — keeps the input value
  // honest without a manual reload.
  useEffect(() => {
    return relay.onCompanyRenamed((next) => {
      setTeamName(next);
      setTeamNameDraft(next);
      setTeamNameSaved(true);
      window.setTimeout(() => setTeamNameSaved(false), 2_000);
    });
  }, []);

  // Subscribe to the relay's `workers` broadcasts and request a fresh
  // roster on mount. The relay also auto-refreshes on every reconnect
  // (see `_connect()` in `web/src/sync/relay.ts`), so role promotions
  // and new joins surface here without a page reload.
  //
  // Web terminals share the relay's `workers` table (the same socket
  // pairs them with a `-web-XXXXXXXX` workerId suffix), but they are
  // not real teammates — they are browser sessions of an existing
  // person. Filter them out here; they live in the dedicated
  // "Sesiones web" panel.
  useEffect(() => {
    const off = relay.onWorkers((list) =>
      setMembers(list.filter((w) => !WEB_TERMINAL_ID_RE.test(w.id))),
    );
    relay.requestWorkers();
    return off;
  }, []);

  async function saveTeamName() {
    const trimmed = teamNameDraft.trim();
    setTeamNameError(null);
    setTeamNameSaved(false);
    if (trimmed.length === 0) {
      setTeamNameError(t('settings.teamErrorEmpty'));
      return;
    }
    if (trimmed.length > 80) {
      setTeamNameError(t('settings.teamErrorTooLong'));
      return;
    }
    if (trimmed === teamName) {
      // No-op — nothing to broadcast.
      return;
    }
    setTeamNameBusy(true);
    try {
      relay.renameCompany(trimmed);
      // Optimistic — the broadcast handler above will set saved=true
      // when the relay echoes back. If the relay rejects (FORBIDDEN
      // / BAD_NAME), we'll see no echo; reset busy after a short
      // window so the button is usable again.
      window.setTimeout(() => setTeamNameBusy(false), 1_500);
    } catch (e) {
      setTeamNameError(e instanceof Error ? e.message : 'Error desconocido');
      setTeamNameBusy(false);
    }
  }

  // Sort: current user first, then creators, admins, members; then
  // by registration time / name as a tiebreaker so the list is stable
  // across re-renders even when `registered_at` is missing.
  const ROLE_WEIGHT: Record<NonNullable<WorkerListEntry['role']>, number> = {
    creator: 0,
    admin: 1,
    member: 2,
  };
  const sortedMembers = [...members].sort((a, b) => {
    if (a.id === identity.technicianId) return -1;
    if (b.id === identity.technicianId) return 1;
    const ra = a.role ?? (a.is_admin ? 'admin' : 'member');
    const rb = b.role ?? (b.is_admin ? 'admin' : 'member');
    const wa = ROLE_WEIGHT[ra];
    const wb = ROLE_WEIGHT[rb];
    if (wa !== wb) return wa - wb;
    if (a.registered_at && b.registered_at) return a.registered_at - b.registered_at;
    return a.name.localeCompare(b.name);
  });

  return (
    <section className="settings-section" data-testid="settings-team">
      <h2 className="settings-h2">{t('settings.team')}</h2>

      {/* ── Subsección 1: Nombre de la empresa ───────────────────── */}
      <h3 className="settings-h3" data-testid="settings-company-name-heading">
        {t('settings.companyName')}
      </h3>
      <label className="settings-team-label" htmlFor="settings-team-name">
        {t('settings.companyName')}
      </label>
      {isCreator ? (
        <div className="settings-team-row">
          <input
            id="settings-team-name"
            className="settings-team-input"
            type="text"
            value={teamNameDraft}
            onChange={(e) => {
              setTeamNameDraft(e.target.value);
              setTeamNameError(null);
              setTeamNameSaved(false);
            }}
            maxLength={80}
            placeholder="Cuadrilla AFM"
            data-testid="settings-team-name-input"
          />
          <button
            type="button"
            className="settings-team-save"
            onClick={saveTeamName}
            disabled={teamNameBusy || teamNameDraft.trim() === teamName}
            data-testid="settings-team-name-save"
          >
            {teamNameSaved ? t('common.saved') : t('common.save')}
          </button>
        </div>
      ) : (
        <p className="settings-team-readonly" data-testid="settings-team-name-readonly">
          {teamName || '—'}
          <span className="settings-team-hint"> · {t('settings.teamReadonlyHint')}</span>
        </p>
      )}
      {teamNameError && (
        <p className="settings-team-error" role="alert" data-testid="settings-team-name-error">
          {teamNameError}
        </p>
      )}

      {/* ── Subsección 2: Miembros ───────────────────────────────── */}
      <h3 className="settings-h3 settings-team-members-heading">
        {t('settings.members')}
      </h3>
      {sortedMembers.length === 0 ? (
        <p
          className="settings-empty"
          data-testid="settings-team-members-empty"
        >
          {t('settings.membersEmpty')}
        </p>
      ) : (
        <ul
          className="settings-member-list"
          data-testid="settings-team-members-list"
        >
          {sortedMembers.map((m) => {
            const role: WebIdentity['role'] =
              m.role ?? (m.is_admin ? 'admin' : 'member');
            const isMe = m.id === identity.technicianId;
            return (
              <li
                key={m.id}
                className="settings-member"
                data-testid={`settings-team-member-${m.id}`}
              >
                <Avatar name={m.name} size="md" ariaHidden />
                <div className="settings-member-info">
                  <span className="settings-member-name">
                    {m.name}
                    {isMe && (
                      <span
                        className="settings-member-you-badge"
                        data-testid={`settings-team-member-${m.id}-you`}
                      >
                        {t('settings.youBadge')}
                      </span>
                    )}
                  </span>
                  <span className="settings-member-meta">
                    <span
                      className={`settings-role-badge settings-role-badge--${role}`}
                    >
                      {ROLE_LABEL[role]}
                    </span>
                    {m.registered_at && (
                      <span className="settings-member-since">
                        {t('settings.memberJoinedAt', {
                          date: formatPairedAt(m.registered_at),
                        })}
                      </span>
                    )}
                  </span>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

/* ───────────────────────── Appearance ──────────────────────────── */

export function SettingsAppearance() {
  const { t } = useTranslation();
  const [locale, setLocaleState] = useState<SupportedLocale>(() => currentLocale());
  const [theme, setTheme] = useState<ThemePreference>(() => readThemePreference());
  const [fontSize, setFontSize] = useState<FontSizePreference>(() => readFontSizePreference());

  function handleLocaleChange(next: SupportedLocale) {
    setLocaleState(next);
    void setLocale(next);
  }

  function persistTheme(next: ThemePreference) {
    setTheme(next);
    try {
      window.localStorage.setItem(THEME_STORAGE_KEY, next);
    } catch {
      // Private mode / quota-exceeded: silently ignore. Best-effort.
    }
    // Fire a synthetic storage event so any provider in the same tab
    // (which doesn't get the native event for same-origin writes) can
    // re-read.
    window.dispatchEvent(new StorageEvent('storage', { key: THEME_STORAGE_KEY, newValue: next }));
  }
  function persistFontSize(next: FontSizePreference) {
    setFontSize(next);
    try {
      window.localStorage.setItem(FONT_SIZE_STORAGE_KEY, next);
    } catch {
      // ditto
    }
    window.dispatchEvent(
      new StorageEvent('storage', { key: FONT_SIZE_STORAGE_KEY, newValue: next }),
    );
  }

  return (
    <section className="settings-section" data-testid="settings-appearance">
      <h2 className="settings-h2">
        <Sun size={20} strokeWidth={2} aria-hidden="true" />
        Apariencia
      </h2>

      <div className="settings-control" role="group" aria-labelledby="settings-theme-label">
        <span id="settings-theme-label" className="settings-control-label">
          Tema
        </span>
        <div className="settings-segmented" data-testid="settings-theme">
          {(
            [
              { key: 'auto', icon: Monitor, label: 'Auto' },
              { key: 'light', icon: Sun, label: 'Claro' },
              { key: 'dark', icon: Moon, label: 'Oscuro' },
            ] as const
          ).map(({ key, icon: Icon, label }) => (
            <button
              key={key}
              type="button"
              className={`settings-segment ${theme === key ? 'settings-segment--active' : ''}`}
              onClick={() => persistTheme(key)}
              aria-pressed={theme === key}
              data-testid={`settings-theme-${key}`}
            >
              <Icon size={16} strokeWidth={2} aria-hidden="true" />
              {label}
            </button>
          ))}
        </div>
      </div>

      <div className="settings-control" role="group" aria-labelledby="settings-fontsize-label">
        <span id="settings-fontsize-label" className="settings-control-label">
          <Type size={16} strokeWidth={2} aria-hidden="true" />
          {t('settings.appearanceFontSize')}
        </span>
        <div className="settings-segmented" data-testid="settings-font-size">
          {(
            [
              { key: 'small', label: t('settings.appearanceFontSizeSmall') },
              { key: 'medium', label: t('settings.appearanceFontSizeMedium') },
              { key: 'large', label: t('settings.appearanceFontSizeLarge') },
            ] as const
          ).map(({ key, label }) => (
            <button
              key={key}
              type="button"
              className={`settings-segment ${
                fontSize === key ? 'settings-segment--active' : ''
              }`}
              onClick={() => persistFontSize(key)}
              aria-pressed={fontSize === key}
              data-testid={`settings-font-size-${key}`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      <div className="settings-control" role="group" aria-labelledby="settings-locale-label">
        <span id="settings-locale-label" className="settings-control-label">
          <Globe size={16} strokeWidth={2} aria-hidden="true" />
          {t('settings.language')}
        </span>
        <div className="settings-segmented" data-testid="settings-locale">
          {SUPPORTED_LOCALES.map((key) => {
            const label =
              key === 'es' ? t('settings.languageEs') : t('settings.languageEn');
            return (
              <button
                key={key}
                type="button"
                className={`settings-segment ${
                  locale === key ? 'settings-segment--active' : ''
                }`}
                onClick={() => handleLocaleChange(key)}
                aria-pressed={locale === key}
                data-testid={`settings-locale-${key}`}
              >
                {label}
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────── Privacy ───────────────────────────── */

export function SettingsPrivacy() {
  return (
    <section className="settings-section" data-testid="settings-privacy">
      <h2 className="settings-h2">
        <ShieldCheck size={20} strokeWidth={2} aria-hidden="true" />
        Privacidad
      </h2>
      <div className="settings-callout" data-testid="settings-screenshot-caveat">
        <Info size={20} strokeWidth={2} aria-hidden="true" />
        <div>
          <p className="settings-callout-title">
            Las capturas de pantalla del navegador no se pueden bloquear
          </p>
          <p className="settings-callout-body">
            A diferencia de la app móvil, el navegador no permite impedir capturas
            ni grabaciones del escritorio. Recuerda cerrar la sesión cuando termines
            y evita usar Cuadrilla web en ordenadores compartidos.
          </p>
        </div>
      </div>
    </section>
  );
}

/* ─────────────────── Sessions (admin only) ─────────────────────── */

export function SettingsSessions({ identity }: PanelProps) {
  const isAdmin = identity.role === 'creator' || identity.role === 'admin';
  const [terminals, setTerminals] = useState<WebTerminalRow[] | null>(null);
  const [terminalsError, setTerminalsError] = useState<string | null>(null);

  useEffect(() => {
    if (!isAdmin) return;
    let cancelled = false;
    async function load() {
      try {
        const res = await fetch(`${RELAY_HTTP_URL}/web-terminals`, {
          headers: {
            'x-company-token': identity.companyToken,
            'x-worker-id': identity.technicianId,
          },
        });
        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`);
        }
        const body = (await res.json()) as { terminals?: WebTerminalRow[] };
        if (cancelled) return;
        setTerminals(body.terminals ?? []);
      } catch (e) {
        if (cancelled) return;
        setTerminalsError(e instanceof Error ? e.message : 'No se pudo cargar la lista');
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [isAdmin, identity.companyToken, identity.technicianId]);

  if (!isAdmin) {
    // Workers landing here directly via URL get bounced to /perfil —
    // the section is admin-only. Rendering nothing keeps the redirect
    // semantics in App.tsx authoritative; here we just defend against
    // a stray render.
    return <Navigate to="/settings/perfil" replace />;
  }

  return (
    <section className="settings-section" data-testid="settings-terminals">
      <h2 className="settings-h2">
        <Globe size={20} strokeWidth={2} aria-hidden="true" />
        Sesiones web conectadas
      </h2>
      <p className="settings-hint" data-testid="settings-terminals-revoke-hint">
        Para revocar una sesión, ábrela desde la app móvil. Por seguridad, las
        sesiones web no pueden cerrar a otras desde aquí.
      </p>
      {terminalsError ? (
        <p className="settings-error" data-testid="settings-terminals-error">
          {terminalsError}
        </p>
      ) : terminals === null ? (
        <p className="settings-loading" data-testid="settings-terminals-loading">
          Cargando…
        </p>
      ) : terminals.length === 0 ? (
        <EmptyState
          testId="settings-terminals-empty"
          variant="noTerminals"
          title="No hay sesiones activas"
          body={
            <>
              Cuando alguien empareje un navegador con tu cuenta,
              aparecerá aquí. Puedes revocar cualquier sesión desde
              el móvil.
            </>
          }
        />
      ) : (
        <ul className="settings-terminal-list" data-testid="settings-terminals-list">
          {terminals.map((t) => (
            <li
              key={t.id}
              className={`settings-terminal-item ${
                t.id === identity.terminalId ? 'settings-terminal-item--current' : ''
              }`}
              data-testid={`settings-terminal-${t.id}`}
            >
              <span className="settings-terminal-ua">{summariseUserAgent(t.user_agent)}</span>
              <span className="settings-terminal-meta">
                Emparejado {formatPairedAt(t.paired_at)}
              </span>
              {t.id === identity.terminalId && (
                <span
                  className="settings-terminal-badge"
                  data-testid={`settings-terminal-${t.id}-current`}
                >
                  Esta sesión
                </span>
              )}
              <Popover.Root>
                <Popover.Trigger asChild>
                  <button
                    type="button"
                    className="settings-terminal-info"
                    aria-label="Detalle de sesión"
                    data-testid={`settings-terminal-${t.id}-info`}
                  >
                    <Info size={16} strokeWidth={2} aria-hidden="true" />
                  </button>
                </Popover.Trigger>
                <Popover.Portal>
                  <Popover.Content
                    className="settings-terminal-popover"
                    sideOffset={6}
                    data-testid={`settings-terminal-${t.id}-popover`}
                  >
                    <p className="settings-terminal-popover-title">User-Agent</p>
                    <p className="settings-terminal-popover-ua">{t.user_agent || '—'}</p>
                    <p className="settings-terminal-popover-meta">
                      Sesión {t.id.slice(0, 8)}…{t.id.slice(-4)}
                    </p>
                    <Popover.Arrow className="settings-terminal-popover-arrow" />
                  </Popover.Content>
                </Popover.Portal>
              </Popover.Root>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/* ───────────── Identidad de empresa (admin only) ────────────────── */

/** Lightweight RFC-5322-ish email check — the same shape mobile uses
 *  on the PdfSender modal. We don't want a full RFC validator here;
 *  we only catch the obvious typos. The relay never validates email
 *  format, so even an invalid value persists fine — this is a UX
 *  guard, not a contract. */
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Hard upper bound on the raw file size we accept for the company
 *  logo. Anything larger is rejected with an inline error before we
 *  even try to read it — the relay payload is encrypted JSON, so a
 *  bloated logo balloons every `branding_sync` blob and every cloud
 *  backup. 256 KB is generous for a PDF header (logos are typically
 *  10-30 KB) without making us the network bottleneck. */
const LOGO_MAX_BYTES = 256 * 1024;

/** Threshold above which we re-encode the image through a canvas to
 *  shrink it. Below this we keep the original bytes verbatim — the
 *  user picked SVG or a tiny PNG, no point round-tripping through
 *  raster compression that would hurt quality. */
const LOGO_COMPRESS_THRESHOLD_BYTES = 64 * 1024;

/** Maximum width/height of the canvas-compressed PNG/JPEG. PDFs print
 *  the logo at ~32-48pt — anything beyond 256px is invisible bandwidth. */
const LOGO_MAX_DIMENSION = 256;

/**
 * Read a picked image as a data URI. If the encoded payload is over
 * `LOGO_COMPRESS_THRESHOLD_BYTES`, redraw it through a 256x256 canvas
 * to bring it down. SVG inputs skip the canvas pass — `<canvas>` would
 * rasterise them and lose the vector advantage.
 */
async function readLogoAsDataUri(file: File): Promise<string> {
  const original = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error ?? new Error('FileReader failed'));
    reader.onload = () => {
      const result = reader.result;
      if (typeof result !== 'string') {
        reject(new Error('FileReader returned non-string'));
        return;
      }
      resolve(result);
    };
    reader.readAsDataURL(file);
  });

  // SVG: keep the original payload — vector data doesn't benefit from
  // canvas downscaling and we'd lose crispness in PDF rendering.
  if (file.type === 'image/svg+xml') return original;

  // Below the threshold: ship as-is. The user already picked a small
  // logo; recompressing would be net-negative on quality.
  if (original.length <= LOGO_COMPRESS_THRESHOLD_BYTES) return original;

  const compressed = await new Promise<string | null>((resolve) => {
    const img = new Image();
    img.onerror = () => resolve(null);
    img.onload = () => {
      const ratio = Math.min(
        LOGO_MAX_DIMENSION / img.naturalWidth,
        LOGO_MAX_DIMENSION / img.naturalHeight,
        1,
      );
      const w = Math.max(1, Math.round(img.naturalWidth * ratio));
      const h = Math.max(1, Math.round(img.naturalHeight * ratio));
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        resolve(null);
        return;
      }
      ctx.drawImage(img, 0, 0, w, h);
      // Preserve transparency for PNGs; JPEGs don't have an alpha
      // channel so we use lossy compression at q=0.85 (visually
      // indistinguishable from q=1 at the size logos render at).
      const mime = file.type === 'image/jpeg' ? 'image/jpeg' : 'image/png';
      const quality = mime === 'image/jpeg' ? 0.85 : undefined;
      try {
        resolve(canvas.toDataURL(mime, quality));
      } catch {
        resolve(null);
      }
    };
    img.src = original;
  });

  return compressed ?? original;
}

/**
 * "Identidad de empresa" panel — editable on the web, syncs to mobile.
 *
 * These are the sender details that appear on the header and footer
 * of every PDF Cuadrilla generates (work orders and signed reports):
 * razón social, NIF, contacto, web, logo. The form mirrors mobile's
 * PdfSenderModal so the two surfaces produce the same wire shape.
 *
 * Logo: editable on both mobile and web. The web uploader reads the
 * picked file as a data URI (FileReader.readAsDataURL), validates the
 * raw size (<= 256 KB), and downscales > 64 KB images through a
 * 256x256 canvas to keep the broadcast payload small. The data URI
 * itself is what travels over `branding_sync` — mobile's outbound
 * filter only strips local-only `file://`/`content://` paths, so a
 * `data:image/...` value flows through both directions intact.
 *
 * Sync: `saveSenderInfo` stamps `updatedAt = Date.now()` and
 * broadcasts a `branding_sync` blob via the team `sync` channel. The
 * inbound dispatcher in `useLiveSync.ts` writes the row directly when
 * the remote `updatedAt` is strictly greater than the local one.
 */
export function SettingsBrandingPanel({ identity }: PanelProps) {
  const { t } = useTranslation();
  const isAdmin = identity.role === 'creator' || identity.role === 'admin';

  const [companyName, setCompanyName] = useState('');
  const [taxId, setTaxId] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [web, setWeb] = useState('');
  const [logoUri, setLogoUri] = useState<string | undefined>(undefined);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [logoError, setLogoError] = useState<string | null>(null);
  const [savedFlash, setSavedFlash] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Hydrate from local + subscribe so a remote update lands in the
  // form without a manual refresh. Subscription fires for both local
  // and remote writes; either way the freshest values from IDB win.
  useEffect(() => {
    let cancelled = false;
    async function load() {
      const { getSenderInfo } = await import('../store/senderInfo');
      const info = await getSenderInfo();
      if (cancelled) return;
      setCompanyName(info?.companyName ?? '');
      setTaxId(info?.taxId ?? '');
      setPhone(info?.phone ?? '');
      setEmail(info?.email ?? '');
      setWeb(info?.web ?? '');
      setLogoUri(info?.logoUri);
      setLoading(false);
    }
    void load();

    let off: (() => void) | null = null;
    void import('../store/senderInfo').then((mod) => {
      if (cancelled) return;
      off = mod.subscribeSenderInfo(() => {
        // Re-hydrate on every change. Cheap (single IDB get) and
        // avoids a stale form when a remote `branding_sync` arrives
        // while the user is on the panel.
        void mod.getSenderInfo().then((info) => {
          if (cancelled || !info) return;
          setCompanyName(info.companyName);
          setTaxId(info.taxId ?? '');
          setPhone(info.phone ?? '');
          setEmail(info.email ?? '');
          setWeb(info.web ?? '');
          setLogoUri(info.logoUri);
        });
      });
    });

    return () => {
      cancelled = true;
      if (off) off();
    };
  }, []);

  if (!isAdmin) return <Navigate to="/settings/perfil" replace />;

  const trimmedName = companyName.trim();
  const canSave = trimmedName.length > 0 && !saving;

  async function handleSave() {
    setEmailError(null);
    if (!canSave) return;
    const trimmedEmail = email.trim();
    if (trimmedEmail && !EMAIL_RE.test(trimmedEmail)) {
      setEmailError(t('settings.brandingEmailInvalid'));
      return;
    }
    setSaving(true);
    try {
      const { saveSenderInfo } = await import('../store/senderInfo');
      await saveSenderInfo(
        {
          companyName: trimmedName,
          taxId: taxId.trim() || undefined,
          phone: phone.trim() || undefined,
          email: trimmedEmail || undefined,
          web: web.trim() || undefined,
          // `logoUri === ''` is a deliberate "user clicked Eliminar" —
          // we send the empty string over the wire so peers clear their
          // local copy. `undefined` means "no change touched this field".
          logoUri,
          updatedAt: Date.now(),
        },
        identity,
      );
      setSavedFlash(true);
      window.setTimeout(() => setSavedFlash(false), 2_000);
    } finally {
      setSaving(false);
    }
  }

  async function handleLogoFile(file: File) {
    setLogoError(null);
    if (file.size > LOGO_MAX_BYTES) {
      setLogoError(t('settings.brandingLogoTooLarge'));
      return;
    }
    try {
      const dataUri = await readLogoAsDataUri(file);
      setLogoUri(dataUri);
    } catch {
      // Reading failed (corrupt file, OOM, etc.). Surface a generic
      // error rather than crashing the panel.
      setLogoError(t('settings.brandingLogoTooLarge'));
    }
  }

  function handleRemoveLogo() {
    setLogoError(null);
    // Empty string is the "removed" sentinel — see handleSave for
    // why we don't use `undefined` here.
    setLogoUri('');
  }

  const hasLogo = typeof logoUri === 'string' && logoUri.length > 0;

  return (
    <section
      className="settings-section settings-section-fiscal"
      data-testid="settings-branding-section"
    >
      <h2 className="settings-h2">
        <Building2 size={20} strokeWidth={2} aria-hidden="true" />
        {t('settings.brandingTitle')}
      </h2>
      <p className="settings-hint" data-testid="settings-branding-hint">
        {t('settings.brandingDescription')}
      </p>

      {loading ? (
        <p className="settings-loading">{t('common.loading')}</p>
      ) : (
        <form
          className="settings-branding-form"
          onSubmit={(e) => {
            e.preventDefault();
            void handleSave();
          }}
        >
          <label className="settings-team-label" htmlFor="settings-branding-company">
            {t('settings.brandingCompanyName')}
          </label>
          <input
            id="settings-branding-company"
            className="settings-team-input"
            type="text"
            value={companyName}
            onChange={(e) => setCompanyName(e.target.value)}
            maxLength={120}
            required
            data-testid="settings-branding-company-input"
          />

          <label className="settings-team-label" htmlFor="settings-branding-taxid">
            {t('settings.brandingTaxId')}
          </label>
          <input
            id="settings-branding-taxid"
            className="settings-team-input"
            type="text"
            value={taxId}
            onChange={(e) => setTaxId(e.target.value)}
            maxLength={32}
            data-testid="settings-branding-taxid-input"
          />

          <label className="settings-team-label" htmlFor="settings-branding-phone">
            {t('settings.brandingPhone')}
          </label>
          <input
            id="settings-branding-phone"
            className="settings-team-input"
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            maxLength={32}
            data-testid="settings-branding-phone-input"
          />

          <label className="settings-team-label" htmlFor="settings-branding-email">
            {t('settings.brandingEmail')}
          </label>
          <input
            id="settings-branding-email"
            className="settings-team-input"
            type="email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              setEmailError(null);
            }}
            maxLength={120}
            data-testid="settings-branding-email-input"
          />
          {emailError && (
            <p className="settings-team-error" role="alert" data-testid="settings-branding-email-error">
              {emailError}
            </p>
          )}

          <label className="settings-team-label" htmlFor="settings-branding-web">
            {t('settings.brandingWeb')}
          </label>
          <input
            id="settings-branding-web"
            className="settings-team-input"
            type="url"
            value={web}
            onChange={(e) => setWeb(e.target.value)}
            maxLength={200}
            data-testid="settings-branding-web-input"
          />

          {/* Logo: editable on web. The hidden input + visible button
              pattern keeps the styling in our hands; the browser's
              default file picker chrome is unstyled and inconsistent
              across OSes. */}
          <span className="settings-team-label">Logo</span>
          <div className="settings-branding-logo-row" data-testid="settings-branding-logo">
            {hasLogo ? (
              <img
                src={logoUri}
                alt=""
                className="settings-branding-logo-preview"
                data-testid="settings-branding-logo-preview"
              />
            ) : (
              <div
                className="settings-branding-logo-placeholder"
                aria-hidden="true"
                data-testid="settings-branding-logo-placeholder"
              >
                <Building2 size={24} strokeWidth={2} aria-hidden="true" />
              </div>
            )}
            <div className="settings-branding-logo-controls">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg,image/svg+xml"
                className="sr-only"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  // Reset the input so picking the same file twice in
                  // a row still fires `onChange`.
                  e.target.value = '';
                  if (file) void handleLogoFile(file);
                }}
                data-testid="settings-branding-logo-input"
              />
              <button
                type="button"
                className="settings-branding-logo-button"
                onClick={() => fileInputRef.current?.click()}
                data-testid="settings-branding-logo-upload"
              >
                {hasLogo
                  ? t('settings.brandingChangeLogo')
                  : t('settings.brandingUploadLogo')}
              </button>
              {hasLogo && (
                <button
                  type="button"
                  className="settings-branding-logo-remove"
                  onClick={handleRemoveLogo}
                  data-testid="settings-branding-logo-remove"
                >
                  <Trash2 size={16} strokeWidth={2} aria-hidden="true" />
                  {t('settings.brandingRemoveLogo')}
                </button>
              )}
            </div>
          </div>
          <p className="settings-branding-logo-hint" data-testid="settings-branding-logo-hint">
            {t('settings.brandingLogoHint')}
          </p>
          {logoError && (
            <p
              className="settings-team-error"
              role="alert"
              data-testid="settings-branding-logo-error"
            >
              {logoError}
            </p>
          )}

          <div className="settings-branding-actions">
            <button
              type="submit"
              className="settings-team-save"
              disabled={!canSave}
              data-testid="settings-branding-save"
            >
              {savedFlash ? t('settings.brandingSaved') : t('common.save')}
            </button>
          </div>
        </form>
      )}
    </section>
  );
}

/* ─────────────────────────── Account ───────────────────────────── */

export function SettingsAccount({ identity, onLogout }: AccountPanelProps) {
  const navigate = useNavigate();
  const [logoutBusy, setLogoutBusy] = useState(false);
  // Hint we render the backup callout here too; if a worker hits
  // /cuenta we still skip BackupStatusCard (it is admin-only).
  const isAdmin = identity.role === 'creator' || identity.role === 'admin';

  async function handleLogout() {
    if (logoutBusy) return;
    // Confirm before wiping local cache. Mobile data is unaffected, but
    // the local IDB + identity will be erased.
    const confirmed = window.confirm(
      '¿Cerrar sesión y borrar caché local?\n\nTus trabajos en el móvil no se verán afectados.',
    );
    if (!confirmed) return;
    setLogoutBusy(true);
    try {
      // Wipe both stores. clearAll drops every IDB store; clearIdentity
      // additionally clears the keys store (separate object store so a
      // future "logout but keep cache" path stays open). Best-effort —
      // navigation still fires even if either throws.
      await Promise.allSettled([clearAll(), clearIdentity()]);
    } finally {
      onLogout?.();
      navigate('/pair', { replace: true });
    }
  }

  return (
    <>
      {isAdmin && <BackupStatusCard />}
      <section
        className="settings-section settings-section-logout"
        data-testid="settings-logout-section"
      >
        <h2 className="settings-h2">Cuenta</h2>
        <button
          type="button"
          className="settings-logout-btn"
          onClick={() => void handleLogout()}
          disabled={logoutBusy}
          data-testid="settings-logout"
        >
          <LogOut size={20} strokeWidth={2} aria-hidden="true" />
          {logoutBusy ? 'Cerrando sesión…' : 'Cerrar sesión en este navegador'}
        </button>
        <p className="settings-logout-hint">
          Borra todos los datos almacenados en este navegador. La sesión sigue
          existiendo en el móvil del propietario hasta que la revoque desde allí.
        </p>
      </section>
    </>
  );
}
